/**
 * GPS Reports Page
 * View trip, route, and summary reports for GPS devices
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  useWindowDimensions,
  Platform,
  Dimensions,
  Share,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Surface, useTheme, Appbar } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { useRouter, useLocalSearchParams } from 'expo-router';
import gpsTrackingAPI from '../../../src/api/services/gpsTracking';
import ReportFilters, { ReportFiltersData } from '../../../src/components/GPS/ReportFilters';
import { Sidebar, SIDEBAR_WIDTH } from '../../../src/components/Sidebar';
import { useUser } from '../../../src/hooks/useUser';
import { useProfile } from '../../../src/hooks/useProfile';
import type { TripReport, SummaryReport, StopsReport, AlertEvent } from '../../../src/types/gps';
import Logo from '../../../src/components/Logo';
import GPSMapView from '../../../src/components/GPS/GPSMapView.web';

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

export default function ReportsPage() {
  const { t } = useTranslation();
  const theme = useTheme();
  const router = useRouter();
  const { width } = useWindowDimensions();
  const isLargeScreen = width > 1024;
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [desktopSidebarVisible, setDesktopSidebarVisible] = useState(isLargeScreen);
  
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<any[]>([]);
  const [currentFilters, setCurrentFilters] = useState<ReportFiltersData | null>(null);
  const [routePositions, setRoutePositions] = useState<any[]>([]);
  const [routeEvents, setRouteEvents] = useState<any[]>([]);
  const [mapRegion, setMapRegion] = useState<any>(null);
  const [initialFilters, setInitialFilters] = useState<ReportFiltersData | null>(null);
  
  const { handleLogout, userData } = useUser();
  const { selectedProfile, effectiveProfile, updateProfile, hasOwner, hasDriver } = useProfile(userData?.groups || []);
  const searchParams = useLocalSearchParams();
  const hasAutoLoadedRef = useRef(false);
  
  useEffect(() => {
    if (isLargeScreen) {
      setDesktopSidebarVisible(true);
      setSidebarOpen(false);
    } else {
      setDesktopSidebarVisible(false);
    }
  }, [isLargeScreen, width]);

  const handleGenerateReport = useCallback(async (filters: ReportFiltersData) => {
    // Check for route report requirement before setting state
    if (filters.reportType === 'routes' && (!filters.deviceId || filters.deviceId === 0)) {
      Alert.alert(
        'Veículo necessário', 
        'O relatório de rotas requer a seleção de um veículo específico. Por favor, selecione um veículo.'
      );
      // Set filters anyway so the empty state can show the proper message
      setCurrentFilters(filters);
      setReportData([]);
      setRoutePositions([]);
      setRouteEvents([]);
      setMapRegion(null);
      return;
    }

    setCurrentFilters(filters);
    setLoading(true);
    setReportData([]);
    setRoutePositions([]);
    setRouteEvents([]);
    setMapRegion(null);

    try {
      const reportFilters = {
        device_id: filters.deviceId,
        from: filters.fromDate,
        to: filters.toDate,
      };

      let data: any[] = [];

      switch (filters.reportType) {
        case 'trips':
          data = await gpsTrackingAPI.reports.getTrips(reportFilters);
          break;
        case 'summary':
          data = await gpsTrackingAPI.reports.getSummary(reportFilters);
          break;
        case 'stops':
          data = await gpsTrackingAPI.reports.getStops(reportFilters);
          break;
        case 'routes':
          
          try {
            const routeData = await gpsTrackingAPI.reports.getRoute(filters.deviceId, reportFilters);
            
            if (!routeData || !routeData.positions) {
              console.warn('No route data returned from API');
              data = [];
              setRoutePositions([]);
              break;
            }
            
            const positions = routeData.positions || [];
            data = positions;
            setRoutePositions(positions);

            if (filters.deviceId) {
              try {
                const events = await gpsTrackingAPI.reports.getEvents({
                  from: filters.fromDate,
                  to: filters.toDate,
                  device_id: filters.deviceId,
                });
                setRouteEvents(Array.isArray(events) ? events : []);
              } catch (eventsError) {
                console.warn('Failed to load route events:', eventsError);
                setRouteEvents([]);
              }
            }
            
            // Calculate map region from positions
            if (positions.length > 0) {
              const lats = positions.map((p: any) => p.latitude).filter((lat: number) => lat != null);
              const lngs = positions.map((p: any) => p.longitude).filter((lng: number) => lng != null);
              
              if (lats.length > 0 && lngs.length > 0) {
                const minLat = Math.min(...lats);
                const maxLat = Math.max(...lats);
                const minLng = Math.min(...lngs);
                const maxLng = Math.max(...lngs);
                
                setMapRegion({
                  latitude: (minLat + maxLat) / 2,
                  longitude: (minLng + maxLng) / 2,
                  latitudeDelta: Math.max((maxLat - minLat) * 1.2, 0.01),
                  longitudeDelta: Math.max((maxLng - minLng) * 1.2, 0.01),
                });
              }
            }
          } catch (routeError) {
            console.error('Route fetch error:', routeError);
            Alert.alert(
              'Erro ao buscar rota',
              'O servidor está demorando muito para responder. Tente novamente em alguns instantes ou selecione um período menor.'
            );
            setLoading(false);
            return;
          }
          break;
        case 'events':
          data = await gpsTrackingAPI.reports.getEvents({
            from: filters.fromDate,
            to: filters.toDate,
            device_id: filters.deviceId || undefined,
          });
          break;
      }

      // Ensure data is an array
      if (!Array.isArray(data)) {
        data = [];
      }
      
      setReportData(data);
    } catch (error: any) {
      console.error('Failed to generate report:', error);
      
      // Check if it's a timeout error
      if (error?.message?.includes('timeout') || error?.code === 'ECONNABORTED') {
        Alert.alert(
          'Tempo esgotado',
          'O servidor está demorando muito para responder. Tente novamente com um período menor.'
        );
      } else if (error?.message?.includes('504') || error?.message?.includes('Gateway')) {
        Alert.alert(
          'Servidor ocupado',
          'O servidor está processando muitos dados. Tente novamente em alguns instantes ou selecione um período menor.'
        );
      } else {
        Alert.alert(
          'Erro',
          'Falha ao gerar relatório. Verifique sua conexão e tente novamente.'
        );
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasAutoLoadedRef.current) return;
    
    const reportTypeParam = typeof searchParams.reportType === 'string' ? searchParams.reportType : undefined;
    const deviceIdParam = typeof searchParams.device_id === 'string' ? Number(searchParams.device_id) : undefined;
    const fromParam = typeof searchParams.from === 'string' ? new Date(searchParams.from) : undefined;
    const toParam = typeof searchParams.to === 'string' ? new Date(searchParams.to) : undefined;
    
    const validReportTypes = ['trips', 'summary', 'stops', 'routes', 'events'];
    const reportType = validReportTypes.includes(reportTypeParam || '')
      ? (reportTypeParam as any)
      : undefined;
    
    const fromDate = fromParam && !isNaN(fromParam.getTime()) ? fromParam : undefined;
    const toDate = toParam && !isNaN(toParam.getTime()) ? toParam : undefined;
    
    if (reportType && fromDate && toDate) {
      const filters: ReportFiltersData = {
        reportType,
        deviceId: deviceIdParam && !isNaN(deviceIdParam) ? deviceIdParam : undefined,
        fromDate,
        toDate,
      };
      setInitialFilters(filters);
      handleGenerateReport(filters);
      hasAutoLoadedRef.current = true;
    }
  }, [searchParams, handleGenerateReport]);

  const handleExportCSV = async () => {
    if (!currentFilters) return;

    try {
      const blob = await gpsTrackingAPI.reports.exportToCSV(
        currentFilters.reportType,
        {
          device_id: currentFilters.deviceId,
          from: currentFilters.fromDate,
          to: currentFilters.toDate,
        }
      );

      // On web, trigger download
      if (Platform.OS === 'web') {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${currentFilters.reportType}_report.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        Alert.alert('Sucesso', 'Relatório exportado com sucesso');
      }
    } catch (error) {
      console.error('Failed to export:', error);
      Alert.alert('Erro', 'Falha ao exportar relatório');
    }
  };

  const formatDuration = (milliseconds: number) => {
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const formatDistance = (meters: number) => {
    return `${(meters / 1000).toFixed(2)} km`;
  };

  const renderReportContent = () => {
    if (loading) {
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color="#007AFF" />
          <Text style={styles.loadingText}>Gerando relatório...</Text>
        </View>
      );
    }

    if (!currentFilters) {
      return (
        <View style={styles.emptyState}>
          <Ionicons name="document-text-outline" size={64} color="#C7C7CC" />
          <Text style={styles.emptyStateTitle}>Selecione os filtros</Text>
          <Text style={styles.emptyStateText}>
            Configure o período e o tipo de relatório acima e clique em "Gerar Relatório"
          </Text>
        </View>
      );
    }

    if (!reportData || reportData.length === 0) {
      // Special message for route reports without vehicle selection
      if (currentFilters.reportType === 'routes' && (!currentFilters.deviceId || currentFilters.deviceId === 0)) {
        return (
          <View style={styles.emptyState}>
            <Ionicons name="car-outline" size={64} color="#C7C7CC" />
            <Text style={styles.emptyStateTitle}>Veículo necessário</Text>
            <Text style={styles.emptyStateText}>
              O relatório de rotas requer a seleção de um veículo específico.{'\n'}
              Por favor, selecione um veículo acima e clique em "Gerar Relatório".
            </Text>
          </View>
        );
      }
      
      return (
        <View style={styles.emptyState}>
          <Ionicons name="folder-open-outline" size={64} color="#C7C7CC" />
          <Text style={styles.emptyStateTitle}>Sem dados</Text>
          <Text style={styles.emptyStateText}>
            Não foram encontrados dados para o período selecionado
          </Text>
        </View>
      );
    }

    // Simple table view for reports
    return (
      <ScrollView style={styles.reportContent}>
        <View style={styles.reportHeader}>
          <Text style={styles.reportTitle}>
            {currentFilters.reportType === 'trips' && 'Relatório de Viagens'}
            {currentFilters.reportType === 'summary' && 'Relatório Sumário'}
            {currentFilters.reportType === 'stops' && 'Relatório de Paragens'}
            {currentFilters.reportType === 'routes' && 'Relatório de Rotas'}
            {currentFilters.reportType === 'events' && 'Relatório de Eventos'}
          </Text>
        </View>

        {currentFilters.reportType === 'trips' && renderTripsTable()}
        {currentFilters.reportType === 'summary' && renderSummaryTable()}
        {currentFilters.reportType === 'stops' && renderStopsTable()}
        {currentFilters.reportType === 'routes' && renderRouteMap()}
        {currentFilters.reportType === 'events' && renderEventsTable()}

        <TouchableOpacity style={styles.exportButton} onPress={handleExportCSV}>
          <Ionicons name="download-outline" size={20} color="#007AFF" />
          <Text style={styles.exportButtonText}>Exportar CSV</Text>
        </TouchableOpacity>
      </ScrollView>
    );
  };

  const renderTripsTable = () => {
    return reportData.map((trip: any, index) => (
      <View key={index} style={styles.reportCard}>
        <Text style={styles.cardTitle}>{trip.deviceName}</Text>
        <View style={styles.cardRow}>
          <Text style={styles.cardLabel}>Distância:</Text>
          <Text style={styles.cardValue}>{formatDistance(trip.distance)}</Text>
        </View>
        <View style={styles.cardRow}>
          <Text style={styles.cardLabel}>Duração:</Text>
          <Text style={styles.cardValue}>{formatDuration(trip.duration)}</Text>
        </View>
        <View style={styles.cardRow}>
          <Text style={styles.cardLabel}>Velocidade Média:</Text>
          <Text style={styles.cardValue}>{(trip.averageSpeed * 1.852).toFixed(1)} km/h</Text>
        </View>
        <View style={styles.cardRow}>
          <Text style={styles.cardLabel}>Início:</Text>
          <Text style={styles.cardValue}>{new Date(trip.startTime).toLocaleString('pt-PT')}</Text>
        </View>
      </View>
    ));
  };

  const renderSummaryTable = () => {
    return reportData.map((summary: any, index) => (
      <View key={index} style={styles.reportCard}>
        <Text style={styles.cardTitle}>{summary.deviceName}</Text>
        <View style={styles.cardRow}>
          <Text style={styles.cardLabel}>Distância Total:</Text>
          <Text style={styles.cardValue}>{formatDistance(summary.distance)}</Text>
        </View>
        <View style={styles.cardRow}>
          <Text style={styles.cardLabel}>Vel. Média:</Text>
          <Text style={styles.cardValue}>{(summary.averageSpeed * 1.852).toFixed(1)} km/h</Text>
        </View>
        <View style={styles.cardRow}>
          <Text style={styles.cardLabel}>Vel. Máxima:</Text>
          <Text style={styles.cardValue}>{(summary.maxSpeed * 1.852).toFixed(1)} km/h</Text>
        </View>
        <View style={styles.cardRow}>
          <Text style={styles.cardLabel}>Horas Motor:</Text>
          <Text style={styles.cardValue}>{(summary.engineHours / 3600000).toFixed(2)}h</Text>
        </View>
      </View>
    ));
  };

  const renderStopsTable = () => {
    return reportData.map((stop: any, index) => (
      <View key={index} style={styles.reportCard}>
        <Text style={styles.cardTitle}>{stop.deviceName}</Text>
        <View style={styles.cardRow}>
          <Text style={styles.cardLabel}>Duração:</Text>
          <Text style={styles.cardValue}>{formatDuration(stop.duration)}</Text>
        </View>
        <View style={styles.cardRow}>
          <Text style={styles.cardLabel}>Início:</Text>
          <Text style={styles.cardValue}>{new Date(stop.startTime).toLocaleString('pt-PT')}</Text>
        </View>
        {stop.address && (
          <View style={styles.cardRow}>
            <Text style={styles.cardLabel}>Local:</Text>
            <Text style={styles.cardValue}>{stop.address}</Text>
          </View>
        )}
      </View>
    ));
  };

  const renderRouteMap = () => {
    if (Platform.OS !== 'web') {
      // Fallback for mobile: show position list
      return (
        <View style={styles.reportCard}>
          <Text style={styles.cardTitle}>Rota - {routePositions.length} posições</Text>
          {routeEvents.length > 0 && (
            <Text style={styles.cardNote}>Alertas no período: {routeEvents.length}</Text>
          )}
          <Text style={styles.cardNote}>
            Visualização de mapa disponível apenas na versão web
          </Text>
          <ScrollView style={{ maxHeight: 300, marginTop: 12 }}>
            {routePositions.slice(0, 10).map((pos: any, idx: number) => (
              <View key={idx} style={styles.positionItem}>
                <Text style={styles.positionText}>
                  {new Date(pos.fixTime).toLocaleTimeString()} - {(pos.speed * 1.852).toFixed(0)} km/h
                </Text>
              </View>
            ))}
          </ScrollView>
        </View>
      );
    }

    if (routePositions.length === 0) {
      return (
        <View style={styles.reportCard}>
          <Text style={styles.cardTitle}>Nenhuma posição encontrada</Text>
          <Text style={styles.cardNote}>
            Não há dados de posição para o período selecionado
          </Text>
        </View>
      );
    }

    // Calculate route statistics
    const totalDistance = routePositions.reduce((sum: number, pos: any, idx: number) => {
      if (idx === 0) return 0;
      const prev = routePositions[idx - 1];
      const R = 6371; // Earth radius in km
      const dLat = ((pos.latitude - prev.latitude) * Math.PI) / 180;
      const dLon = ((pos.longitude - prev.longitude) * Math.PI) / 180;
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((prev.latitude * Math.PI) / 180) *
          Math.cos((pos.latitude * Math.PI) / 180) *
          Math.sin(dLon / 2) *
          Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return sum + R * c;
    }, 0);

    // Calculate time first
    const startTime = new Date(routePositions[0].fixTime);
    const endTime = new Date(routePositions[routePositions.length - 1].fixTime);
    const totalDuration = (endTime.getTime() - startTime.getTime()) / 1000 / 60; // minutes
    
    // Traccar returns speed in knots, convert to km/h (1 knot = 1.852 km/h)
    const speeds = routePositions.map((p: any) => p.speed * 1.852).filter((s: number) => s > 0);
    const maxSpeed = speeds.length > 0 ? Math.max(...speeds) : 0;
    
    // Calculate average speed correctly: distance / time (not average of speed readings)
    // Convert total duration from minutes to hours
    const totalDurationHours = totalDuration / 60;
    const avgSpeed = totalDurationHours > 0 ? totalDistance / totalDurationHours : 0;
    
    // Calculate moving time (only when speed > 5 km/h threshold)
    const MOVING_SPEED_THRESHOLD = 5; // km/h
    let movingTime = 0;
    let stoppedTime = 0;
    
    for (let i = 1; i < routePositions.length; i++) {
      const prev = routePositions[i - 1];
      const curr = routePositions[i];
      const prevTime = new Date(prev.fixTime);
      const currTime = new Date(curr.fixTime);
      const segmentDuration = (currTime.getTime() - prevTime.getTime()) / 1000 / 60; // minutes
      
      // Convert speed from knots to km/h for comparison
      const speedKmh = (curr.speed || 0) * 1.852;
      const prevSpeedKmh = (prev.speed || 0) * 1.852;
      const avgSpeedKmh = (speedKmh + prevSpeedKmh) / 2;
      
      if (avgSpeedKmh > MOVING_SPEED_THRESHOLD) {
        movingTime += segmentDuration;
      } else {
        stoppedTime += segmentDuration;
      }
    }

    const alertMarkers = buildAlertMarkers();

    return (
      <View style={styles.routeMapContainer}>
        {/* Route Statistics */}
        <View style={styles.routeStats}>
          <View style={styles.statItem}>
            <Ionicons name="location-outline" size={20} color="#007AFF" />
            <View>
              <Text style={styles.statLabel}>Distância Total</Text>
              <Text style={styles.statValue}>{totalDistance.toFixed(2)} km</Text>
            </View>
          </View>
          <View style={styles.statItem}>
            <Ionicons name="flash-outline" size={20} color="#FF3B30" />
            <View>
              <Text style={styles.statLabel}>Vel. Máxima</Text>
              <Text style={styles.statValue}>{maxSpeed.toFixed(0)} km/h</Text>
            </View>
          </View>
        </View>

        {/* Map with Route */}
        <View style={styles.mapContainer}>
          <GPSMapView
            style={styles.map}
            region={mapRegion}
            positions={[]}
            showsUserLocation={false}
            routePositions={routePositions}
            alertMarkers={alertMarkers as any}
            showRoute={true}
          />
          <View style={styles.mapOverlay}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <View style={{ width: 12, height: 12, borderRadius: 6, backgroundColor: '#34C759' }} />
              <Text style={{ fontSize: 11, color: '#8E8E93' }}>Início</Text>
              <View style={{ width: 20, height: 3, backgroundColor: '#007AFF', marginHorizontal: 4 }} />
              <View style={{ width: 12, height: 12, borderRadius: 6, backgroundColor: '#FF3B30' }} />
              <Text style={{ fontSize: 11, color: '#8E8E93' }}>Fim</Text>
              <Text style={[styles.mapNote, { marginLeft: 8 }]}>
                ({routePositions.length} pontos)
              </Text>
            </View>
          </View>
        </View>
      </View>
    );
  };

  const getAlertIcon = (eventType: string) => {
    const type = eventType.toLowerCase();
    if (type.includes('geofence')) return 'location-outline';
    if (type.includes('speed')) return 'speedometer-outline';
    if (type.includes('offline') || type.includes('online')) return 'wifi-outline';
    if (type.includes('ignition')) return 'key-outline';
    if (type.includes('battery')) return 'battery-half-outline';
    if (type.includes('sos')) return 'warning-outline';
    return 'notifications-outline';
  };

  const getAlertColor = (eventType: string, status: string) => {
    if (status === 'acknowledged') return '#8E8E93';
    
    const type = eventType.toLowerCase();
    if (type.includes('sos') || type.includes('speed')) return '#FF3B30';
    if (type.includes('offline')) return '#FF9500';
    if (type.includes('online')) return '#34C759';
    return '#007AFF';
  };

  const translateEventType = (eventType: string): string => {
    const translations: Record<string, string> = {
      'deviceOnline': 'Dispositivo Online',
      'deviceOffline': 'Dispositivo Offline',
      'deviceUnknown': 'Estado Desconhecido',
      'deviceMoving': 'Em Movimento',
      'deviceStopped': 'Parado',
      'deviceOverspeed': 'Excesso de Velocidade',
      'deviceFuelDrop': 'Queda de Combustível',
      'commandResult': 'Resultado de Comando',
      'geofenceEnter': 'Entrada em Geocerca',
      'geofenceExit': 'Saída de Geocerca',
      'alarm': 'Alarme',
      'ignitionOn': 'Ignição Ligada',
      'ignitionOff': 'Ignição Desligada',
      'maintenance': 'Manutenção',
      'textMessage': 'Mensagem de Texto',
      'driverChanged': 'Condutor Alterado',
    };
    
    return translations[eventType] || eventType;
  };

  const isOverspeedEvent = (eventType: string) => {
    const normalized = eventType.toLowerCase();
    return normalized.includes('overspeed') || normalized.includes('speed');
  };

  const getEventTime = (event: any) => {
    const raw = event.eventTime || event.serverTime || event.deviceTime;
    const parsed = raw ? new Date(raw) : null;
    return parsed && !isNaN(parsed.getTime()) ? parsed : null;
  };

  const buildRouteLinkParams = (event: any) => {
    const eventTime = getEventTime(event);
    if (!eventTime || !event.device_id) return null;
    const windowMinutes = 30;
    const from = new Date(eventTime.getTime() - windowMinutes * 60 * 1000);
    const to = new Date(eventTime.getTime() + windowMinutes * 60 * 1000);
    return {
      pathname: '/dashboard/gps/reports',
      params: {
        reportType: 'routes',
        device_id: String(event.device_id),
        from: from.toISOString(),
        to: to.toISOString(),
      },
    };
  };

  const buildAlertMarkers = () => {
    if (!routePositions.length || !routeEvents.length) return [];
    const positionsWithTime = routePositions
      .map((pos: any) => ({
        ...pos,
        _time: pos.fixTime ? new Date(pos.fixTime).getTime() : null,
      }))
      .filter((pos: any) => pos._time !== null);

    return routeEvents
      .map((event: any) => {
        const eventTime = getEventTime(event);
        if (!eventTime) return null;
        const eventTs = eventTime.getTime();
        let closest: any = null;
        let closestDelta = Number.POSITIVE_INFINITY;
        for (const pos of positionsWithTime) {
          const delta = Math.abs(pos._time - eventTs);
          if (delta < closestDelta) {
            closestDelta = delta;
            closest = pos;
          }
        }
        if (!closest) return null;

        // Speed from closest position (knots → km/h)
        const speedKmh = closest.speed ? closest.speed * 1.852 : undefined;
        
        // Speed limit from event attributes (Traccar sends in knots)
        const eventAttrs = event.attributes || {};
        const speedLimit = eventAttrs.speedLimit 
          ? eventAttrs.speedLimit * 1.852 
          : undefined;

        // Device name
        const deviceName = event.device_name || (event.deviceId ? `Dispositivo ${event.deviceId}` : undefined);

        // Address from closest position attributes
        const address = closest.address || eventAttrs.address || undefined;

        return {
          latitude: closest.latitude,
          longitude: closest.longitude,
          title: translateEventType(event.type || 'alerta'),
          description: eventTime.toLocaleString('pt-PT'),
          color: isOverspeedEvent(event.type || '') ? '#FF3B30' : '#FF9500',
          speed: speedKmh,
          speedLimit: speedLimit,
          eventType: event.type,
          deviceName: deviceName,
          address: address,
          attributes: eventAttrs,
        };
      })
      .filter(Boolean);
  };

  const renderEventsTable = () => {
    return reportData.map((event: any, index) => {
      // Traccar event format: { id, type, eventTime, deviceId, positionId, geofenceId, maintenanceId, attributes }
      const eventType = event.type || 'unknown';
      const deviceName = event.device_name || `Dispositivo ${event.deviceId}`;
      const eventTypeTranslated = translateEventType(eventType);
      const routeParams = isOverspeedEvent(eventType) ? buildRouteLinkParams(event) : null;
      
      return (
        <View key={event.id || index} style={[styles.reportCard, styles.eventCard]}>
          <View style={styles.eventHeader}>
            <View style={styles.eventTitleRow}>
              <Ionicons
                name={getAlertIcon(eventType) as any}
                size={24}
                color={getAlertColor(eventType, '')}
              />
              <View style={styles.eventInfo}>
                <Text style={styles.eventDevice}>{deviceName}</Text>
                <Text style={styles.eventType}>{eventTypeTranslated}</Text>
              </View>
            </View>
          </View>

          <View style={styles.eventFooter}>
            <View style={styles.eventFooterItem}>
              <Ionicons name="time-outline" size={14} color="#8E8E93" />
              <Text style={styles.eventFooterText}>
                {getEventTime(event)?.toLocaleString('pt-PT') || 'N/A'}
              </Text>
            </View>
            {routeParams && (
              <TouchableOpacity
                style={styles.eventLinkButton}
                onPress={() => router.push(routeParams as any)}
              >
                <Ionicons name="map-outline" size={16} color="#007AFF" />
                <Text style={styles.eventLinkText}>Ver rota</Text>
              </TouchableOpacity>
            )}
          </View>

          {/* Show event attributes if available */}
          {event.attributes && Object.keys(event.attributes).length > 0 && (
            <View style={{ marginTop: 8 }}>
              {Object.entries(event.attributes).map(([key, value]: [string, any]) => {
                // Traccar returns speed values in knots, convert to km/h
                const isSpeedAttr = key.toLowerCase().includes('speed');
                const displayValue = isSpeedAttr && typeof value === 'number'
                  ? `${(value * 1.852).toFixed(1)} km/h`
                  : String(value);
                const displayKey = isSpeedAttr
                  ? (key === 'speed' ? 'Velocidade' : key === 'speedLimit' ? 'Limite de Velocidade' : key)
                  : key;
                return (
                  <View key={key} style={styles.cardRow}>
                    <Text style={styles.cardLabel}>{displayKey}:</Text>
                    <Text style={styles.cardValue}>{displayValue}</Text>
                  </View>
                );
              })}
            </View>
          )}
        </View>
      );
    });
  };

  const renderHeader = () => {
    if (isLargeScreen) return null;
    
    return (
      <Appbar.Header style={{ 
        height: SCREEN_HEIGHT * 0.1, 
        backgroundColor: theme.colors.surface,
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1
      }}>
        <Appbar.Action icon="menu" onPress={() => setSidebarOpen(true)} color={theme.colors.onSurface} />
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Logo 
            size={Platform.OS === 'web' ? 'medium' : 'mobile'} 
            style={{ maxWidth: Platform.OS === 'web' ? 200 : 250 }} 
          />
        </View>
        <View style={{ width: 48 }} />
      </Appbar.Header>
    );
  };

  return (
    <View style={styles.outerContainer}>
      {/* Desktop Sidebar */}
      {desktopSidebarVisible && (
        <Sidebar
          key={`desktop-sidebar-${width}`}
          isOpen={true}
          onClose={() => {}}
          screenWidth={width}
          onLogout={handleLogout}
          isOwner={true}
          isDriver={hasDriver}
          selectedProfile={effectiveProfile}
          onSwitchProfile={hasOwner && hasDriver ? (newProfile: 'Owner' | 'Driver') => { updateProfile(newProfile); } : undefined}
          onRegisterDriver={!hasDriver ? () => router.push('/register/driver?skipLogin=true') : undefined}
        />
      )}

      {/* Main Content */}
      <Surface style={{ flex: 1, backgroundColor: theme.colors.background, ...(desktopSidebarVisible ? { marginLeft: SIDEBAR_WIDTH } : {}) }}>
        {renderHeader()}
        
        <View style={[styles.container, !isLargeScreen && { marginTop: SCREEN_HEIGHT * 0.1 }]}>
          {/* Filters */}
          <ReportFilters
            onApply={handleGenerateReport}
            initialFilters={initialFilters || undefined}
          />
          
          {/* Report Content */}
          <View style={styles.content}>
            {renderReportContent()}
          </View>
        </View>
      </Surface>

      {/* Mobile Sidebar */}
      {!isLargeScreen && (
        <Sidebar
          key={`mobile-sidebar-${width}`}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          screenWidth={width}
          onLogout={handleLogout}
          isOwner={true}
          isDriver={hasDriver}
          selectedProfile={effectiveProfile}
          onSwitchProfile={hasOwner && hasDriver ? (newProfile: 'Owner' | 'Driver') => { updateProfile(newProfile); } : undefined}
          onRegisterDriver={!hasDriver ? () => router.push('/register/driver?skipLogin=true') : undefined}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    flexDirection: 'row',
  },
  container: {
    flex: 1,
    backgroundColor: '#F2F2F7',
  },
  header: {
    backgroundColor: '#FFFFFF',
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
    gap: 12,
  },
  menuButton: {
    padding: 4,
  },
  headerContent: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: '#000000',
  },
  generateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  generateButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#007AFF',
  },
  typeSelector: {
    flexDirection: 'row',
    padding: 16,
    gap: 8,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
  },
  typeButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E5EA',
    backgroundColor: '#F9F9F9',
  },
  typeButtonActive: {
    backgroundColor: '#E5F1FF',
    borderColor: '#007AFF',
  },
  typeButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#8E8E93',
  },
  typeButtonTextActive: {
    color: '#007AFF',
    fontWeight: '600',
  },
  content: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#8E8E93',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  emptyStateTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#000000',
    marginTop: 16,
    textAlign: 'center',
  },
  emptyStateText: {
    fontSize: 14,
    color: '#8E8E93',
    marginTop: 8,
    textAlign: 'center',
    paddingHorizontal: 20,
  },
  featuresContainer: {
    width: '100%',
    marginTop: 32,
    gap: 16,
  },
  featuresTitle: {
    fontSize: 17,
    fontWeight: '600',
    color: '#000000',
    marginBottom: 8,
  },
  featureItem: {
    flexDirection: 'row',
    gap: 12,
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E5EA',
  },
  featureText: {
    flex: 1,
  },
  featureTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000000',
    marginBottom: 4,
  },
  featureDescription: {
    fontSize: 14,
    color: '#8E8E93',
    lineHeight: 20,
  },
  ctaButton: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 24,
    paddingVertical: 14,
    borderRadius: 12,
    marginTop: 24,
  },
  ctaButtonDisabled: {
    backgroundColor: '#8E8E93',
  },
  ctaButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  reportContent: {
    flex: 1,
    padding: 16,
  },
  reportHeader: {
    marginBottom: 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5EA',
  },
  reportTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#000000',
    marginBottom: 4,
  },
  reportSubtitle: {
    fontSize: 14,
    color: '#8E8E93',
  },
  reportCard: {
    backgroundColor: '#FFFFFF',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E5EA',
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000000',
    marginBottom: 12,
  },
  cardRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  cardLabel: {
    fontSize: 14,
    color: '#8E8E93',
  },
  cardValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#000000',
  },
  cardNote: {
    fontSize: 12,
    color: '#8E8E93',
    fontStyle: 'italic',
    marginTop: 8,
  },
  exportButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#FFFFFF',
    paddingVertical: 14,
    borderRadius: 10,
    marginTop: 16,
    marginBottom: 32,
    borderWidth: 1,
    borderColor: '#007AFF',
  },
  exportButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#007AFF',
  },
  // Route Map Styles
  routeMapContainer: {
    flex: 1,
  },
  routeStats: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 16,
    backgroundColor: '#FFFFFF',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E5EA',
  },
  statItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    minWidth: '45%',
  },
  statLabel: {
    fontSize: 12,
    color: '#8E8E93',
  },
  statValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000000',
  },
  mapContainer: {
    height: 500,
    backgroundColor: '#E5E5EA',
    borderRadius: 12,
    overflow: 'hidden',
    position: 'relative',
  },
  map: {
    flex: 1,
  },
  mapOverlay: {
    position: 'absolute',
    bottom: 16,
    left: 16,
    right: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E5EA',
  },
  mapNote: {
    fontSize: 12,
    color: '#8E8E93',
    textAlign: 'center',
  },
  positionItem: {
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#F2F2F7',
  },
  positionText: {
    fontSize: 13,
    color: '#000000',
  },
  // Events Styles
  eventCard: {
    borderLeftWidth: 4,
    borderLeftColor: '#007AFF',
  },
  eventHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  eventTitleRow: {
    flexDirection: 'row',
    gap: 12,
    flex: 1,
  },
  eventInfo: {
    flex: 1,
  },
  eventDevice: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000000',
    marginBottom: 2,
  },
  eventType: {
    fontSize: 13,
    color: '#8E8E93',
    textTransform: 'capitalize',
  },
  eventMessage: {
    fontSize: 14,
    color: '#000000',
    marginBottom: 12,
    lineHeight: 20,
  },
  eventFooter: {
    flexDirection: 'row',
    gap: 16,
    flexWrap: 'wrap',
    marginBottom: 8,
    alignItems: 'center',
  },
  eventLinkButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E5E5EA',
    backgroundColor: '#FFFFFF',
  },
  eventLinkText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#007AFF',
  },
  eventFooterItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  eventFooterText: {
    fontSize: 12,
    color: '#8E8E93',
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: '#E5E5EA',
  },
  statusBadgeAcknowledged: {
    backgroundColor: '#E8F5E9',
  },
  statusBadgeSent: {
    backgroundColor: '#E3F2FD',
  },
  statusBadgeFailed: {
    backgroundColor: '#FFEBEE',
  },
  statusBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#000000',
    textTransform: 'uppercase',
  },
  channelsRow: {
    flexDirection: 'row',
    gap: 6,
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#F2F2F7',
  },
  channelBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    backgroundColor: '#F2F2F7',
  },
  channelText: {
    fontSize: 10,
    fontWeight: '500',
    color: '#8E8E93',
    textTransform: 'uppercase',
  },
});

